import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { t } from 'i18next'
import { toast } from 'react-toastify'

import {
  ApiResponseError,
  NetworkError,
  PaginationError,
} from '@/lib/api/errors'

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: { skipGlobalErrorToast?: boolean }
  }
}

export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof NetworkError || error instanceof PaginationError) {
    return false
  }
  if (
    error != null &&
    typeof error === 'object' &&
    'name' in error &&
    error.name === 'AbortError'
  ) {
    return false
  }
  if (error instanceof ApiResponseError && error.statusCode < 500) return false
  return failureCount < 2
}

export function authAndInstanceRedirect(error: unknown): string | undefined {
  if (!(error instanceof ApiResponseError) || typeof window === 'undefined') {
    return undefined
  }
  if (error.statusCode === 404 && error.code === 'not_found.instance') {
    return '/'
  }
  const visit = error.details?.visit
  if (
    error.statusCode !== 401 ||
    !['auth_missing', 'auth_check_required'].includes(error.code || '') ||
    !visit
  ) {
    return undefined
  }
  const timestampKey = 'iam-redirection-timestamp'
  try {
    const previous = Number(sessionStorage.getItem(timestampKey))
    if (previous && Date.now() - previous < 5000) return undefined
    sessionStorage.setItem(timestampKey, String(Date.now()))
  } catch {
    // Authentication still works when browser storage is unavailable.
  }
  const authLocation = `${window.location.origin}/auth`
  return visit
    .replace('$RETURN_TO', encodeURIComponent(window.location.href))
    .replace('$JR_API_LOCATION/iam/auth', authLocation)
    .replace('$JR_AUTH_LOCATION', authLocation)
}

function handleAuthAndInstance(error: unknown): boolean {
  const target = authAndInstanceRedirect(error)
  if (!target) return false
  window.location.assign(target)
  return true
}

function reportFailure(error: unknown, action: boolean): void {
  if (handleAuthAndInstance(error)) return
  if (
    error != null &&
    typeof error === 'object' &&
    'name' in error &&
    error.name === 'AbortError'
  ) {
    return
  }
  const systemError =
    error instanceof NetworkError ||
    (error instanceof ApiResponseError && error.statusCode >= 500)
  if (!systemError && !action) return
  const key = systemError ? 'internalServerError' : 'unknownError'
  toast.error(t(`errors.${key}.headline`), { toastId: `query-${key}` })
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => reportFailure(error, false),
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (mutation.meta?.skipGlobalErrorToast) {
          handleAuthAndInstance(error)
          return
        }
        reportFailure(error, true)
      },
    }),
    defaultOptions: {
      queries: { staleTime: 30_000, retry: shouldRetry },
      mutations: { retry: shouldRetry },
    },
  })
}

// The browser application has one cache, shared by hooks and imperative actions.
export const queryClient = createQueryClient()
